/**
 * Error tracking. ADR-0010, FND-002 (`FIX`), FND-012 (the transport).
 *
 * **The one place the rebuild is knowingly not at parity.** The old app has no tracking
 * of any kind, and Phase 7 requires rollback criteria written down before the switch — a
 * criterion nobody can measure is not a criterion. For a check-in app used once a week in
 * a loft, a client broken for part of the choir is otherwise invisible for a week.
 *
 * **The service is Sentry; the client is these hundred lines.** `@sentry/browser` was
 * installed, built and measured on 2026-09-08: 447 kB raw, **147 kB gzipped**, as a lazy
 * chunk, against an app that is 81 kB gzipped whole. Nearly twice the app, for a phone on
 * mobile data in a choir loft, to report an error that has already happened. What is sent
 * instead is Sentry's own envelope over `fetch` — the same endpoint, the same DSN, the
 * same project — and it costs nothing measurable. See FND-012: if the grouping or the
 * stack traces this produces turn out to be unusable in practice, the SDK is one
 * `npm install` away and this file becomes its wrapper.
 *
 * Three rules this module exists to enforce, in one place:
 *
 * 1. **No PII.** The Telegram user id is hashed before it is used as a scope id. Display
 *    names, birthdays, notes and attendance never leave the app. Appendix C item 10.
 * 2. **Tagged with the client**, so a bug that only happens on one Telegram client is
 *    identifiable at all. Appendix C item 26.
 * 3. **Absent unless configured.** With no DSN nothing is sent, and the transport folds
 *    away at build time.
 */

import { clientVersion, platform } from './telegram';

/**
 * The app version, so a report says which build it came from. Set at build time from the
 * commit; `dev` when nothing set it.
 */
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';

/**
 * A Sentry DSN, which is public by construction — it is a write-only ingest key. It is
 * the only `VITE_*` variable this app has that looks like a credential and is not one;
 * `scripts/scan-bundle.sh` knows it and every server-only name it must never find.
 */
const DSN: string = import.meta.env.VITE_SENTRY_DSN ?? '';

/**
 * A stable, non-reversible id for one person, so two reports can be recognised as the
 * same person's without the tracker ever holding a Telegram user id.
 *
 * FNV-1a over the id with the release as a salt. Not a cryptographic hash and not claimed
 * to be one: it exists so that the tracker's database cannot be joined against
 * Telegram's, and a choir is small enough that a keyed hash would be brute-forceable
 * anyway. What it does guarantee is that nothing recognisable leaves the device.
 */
export function scopeId(telegramUserId: number): string {
  const input = `${APP_VERSION}:${telegramUserId}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export type Tags = {
  /** `ios` · `android` · `tdesktop` · `macos` · `weba` · `webk`, or `''` outside Telegram. */
  platform: string;
  /** The Bot API version the client reports, or `''`. */
  client_version: string;
  app_version: string;
};

export function tags(): Tags {
  return { platform: platform(), client_version: clientVersion(), app_version: APP_VERSION };
}

export type Dsn = { url: string; key: string };

/**
 * `https://<key>@<host>/<project>` → the envelope endpoint and the ingest key.
 * Returns null for anything unparseable, so a mistyped variable disables tracking rather
 * than throwing on the first frame.
 */
export function parseDsn(raw: string): Dsn | null {
  try {
    const parsed = new URL(raw);
    const project = parsed.pathname.replace(/^\//, '');
    if (parsed.username === '' || project === '') return null;
    return {
      url: `${parsed.protocol}//${parsed.host}/api/${project}/envelope/?sentry_version=7&sentry_key=${parsed.username}`,
      key: parsed.username,
    };
  } catch {
    return null;
  }
}

function describe(error: unknown): { type: string; value: string; stack: string } {
  if (error instanceof Error) {
    return { type: error.name, value: error.message, stack: error.stack ?? '' };
  }
  return { type: 'UnknownError', value: String(error), stack: '' };
}

function eventId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** Sentry's envelope: a header line, an item header line, and the payload. */
export function envelope(
  error: unknown,
  context: Record<string, string> | undefined,
  scope: string | null,
): string {
  const { type, value, stack } = describe(error);
  const id = eventId();
  const event = {
    event_id: id,
    timestamp: Date.now() / 1000,
    platform: 'javascript',
    level: 'error',
    release: APP_VERSION,
    tags: { ...tags(), ...context },
    // No user object at all unless somebody has been identified, and then only the hash.
    ...(scope === null ? {} : { user: { id: scope } }),
    exception: { values: [{ type, value }] },
    // The stack as text rather than as parsed frames: parsing them is what the SDK's
    // weight is mostly for, and a readable string groups poorly but reads fine.
    extra: { stack },
  };
  return [
    JSON.stringify({ event_id: id, sent_at: new Date().toISOString() }),
    JSON.stringify({ type: 'event' }),
    JSON.stringify(event),
  ].join('\n');
}

let endpoint: Dsn | null = null;
let scope: string | null = null;

/**
 * Reports raised in one session, capped. The interesting failure is the first one, and an
 * app erroring in a loop must not spend a person's data reporting itself.
 */
let sent = 0;
const SEND_LIMIT = 20;

export function capture(error: unknown, context?: Record<string, string>): void {
  if (endpoint === null || sent >= SEND_LIMIT) return;
  sent += 1;
  try {
    void fetch(endpoint.url, {
      method: 'POST',
      // The envelope endpoint accepts this without a preflight, which matters: a
      // preflight is a second round trip on a connection that has just failed at
      // something.
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: envelope(error, context, scope),
      // The report must never keep the app waiting, and must survive the page going away.
      keepalive: true,
    }).catch(() => {
      // A failure to report a failure is not a failure.
    });
  } catch {
    // Same.
  }
}

/** Called once the person is known. Takes the raw id and hashes it here, never outside. */
export function identify(telegramUserId: number): void {
  scope = scopeId(telegramUserId);
}

/**
 * Wire the global handlers.
 *
 * Attached synchronously and always, DSN or not: an error thrown in the first frame must
 * be caught, and in a build with no DSN `capture` is a no-op rather than a crash.
 */
export function initTracking(): void {
  endpoint = DSN === '' ? null : parseDsn(DSN);

  window.addEventListener('error', (event) => {
    capture(event.error ?? event.message, { kind: 'window.error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    capture(event.reason, { kind: 'unhandledrejection' });
  });
}
